import { getFoodNamesByIds } from '../../../data/foods';
import { getScenarioDisplayLabel } from '../../../data/maps';
import { getBodyImageSelectedFigure } from '../../body-image/utils';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getExposureConfigFromSession } from '../../../utils/extractSessionObject';
import { getPlannedExposureGroups } from '../../../utils/exposureMultiItem';
import { parseConfiguration } from '../../../utils/parseConfiguration';

export type ConfigSummaryRow = {
  label: string;
  value: string;
};

const yesNo = (value: boolean | undefined): string => (value ? 'sì' : 'no');

const scenarioLabel = (session: DeviceConfigurationApi): string =>
  getScenarioDisplayLabel(session.type, session.scenario) ||
  session.scenario ||
  '—';

const formatExposureRows = (
  session: DeviceConfigurationApi
): ConfigSummaryRow[] => {
  const exposure = getExposureConfigFromSession(session);
  const groups = getPlannedExposureGroups(exposure);
  const totalItems = groups.reduce((sum, g) => sum + g.itemIds.length, 0);
  const isMulti = Boolean(exposure.isMultiItem);

  return [
    { label: 'Mappa', value: scenarioLabel(session) },
    { label: 'Spawn', value: exposure.userSpawnPosition ?? '1' },
    { label: 'Tutorial', value: yesNo(exposure.hasTutorial) },
    { label: 'Buffet / multi-item', value: yesNo(isMulti) },
    {
      label: 'Elementi',
      value: `${totalItems} totali${isMulti ? ` · ${groups.length} gruppi` : ''}`,
    },
    ...groups.map(group => ({
      label: group.label,
      value: getFoodNamesByIds(group.itemIds) || '—',
    })),
  ];
};

const asConfigRecord = (config: unknown): Record<string, unknown> => {
  const parsed = parseConfiguration(config);
  if (parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)) {
    return parsed as Record<string, unknown>;
  }
  return {};
};

const formatPerformanceRows = (
  session: DeviceConfigurationApi
): ConfigSummaryRow[] => {
  const config = asConfigRecord(session.config);
  const currentState =
    typeof config.currentState === 'string' ? config.currentState : '—';
  const variation =
    typeof config.variation === 'number' ? `${config.variation}%` : '—';
  const duration =
    typeof config.duration === 'number'
      ? `${config.duration}s`
      : typeof config.maxDuration === 'number'
        ? `${config.maxDuration}s`
        : '—';

  return [
    { label: 'Scenario', value: scenarioLabel(session) },
    { label: 'Livello', value: currentState },
    { label: 'Variazione', value: variation },
    { label: 'Durata', value: duration },
  ];
};

const formatBodyImageRows = (
  session: DeviceConfigurationApi
): ConfigSummaryRow[] => {
  const config = asConfigRecord(session.config);
  const mode = typeof config.mode === 'string' ? config.mode : 'body-image';
  const figure = getBodyImageSelectedFigure(session.config);

  return [
    { label: 'Mode', value: mode },
    {
      label: 'Figura selezionata',
      value: figure != null ? String(figure) : '—',
    },
  ];
};

/** Pure formatter: session → human-readable rows for the debug summary. */
export const formatSessionConfig = (
  session: DeviceConfigurationApi | null | undefined
): ConfigSummaryRow[] => {
  if (!session) return [];

  if (session.type === 'exposure') return formatExposureRows(session);
  if (session.type === 'performance') return formatPerformanceRows(session);
  if (session.type === 'body_image') return formatBodyImageRows(session);

  return [
    { label: 'Tipo', value: session.type || '—' },
    { label: 'Scenario', value: session.scenario || '—' },
  ];
};

export const getSessionConfigRawJson = (
  session: DeviceConfigurationApi | null | undefined
): string => {
  if (!session?.config) return '';
  try {
    return JSON.stringify(session.config, null, 2);
  } catch {
    return String(session.config);
  }
};
